import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { loadCorrections, loadMeta, type Corrections, type Meta } from '../data';
import { fmtLong } from '../format';
import { navigate } from '../router';
import ROUTES from '../routes.json';
import { Pinned } from './Chrome';
import './trust.css';

// The four trust pages (README section 7; brief, "Trust pages"): one shared layout, a
// 154px label gutter beside a justified prose column, 880px wide, hairlines between.
//
// EVERY SENTENCE HERE IS A CLAIM ABOUT THE SITE, and the brief's rule applies to it as to
// the grid: if it is not true, it is not said. The prototype's figures were made up — its
// $4,180 from 1,204 readers and its six corrections — and none of them is here. Some
// sentences describe the site as it works on launch day (the address lookup, the error
// form, the nightly job); CLAUDE.md lists them, and Phase 7 checks each against the
// running site. A feature that ships differently changes its sentence in the same commit.

export type TrustPage = 'how' | 'corrections' | 'pays' | 'privacy';

// The names come from routes.json, which the site's server also reads for a shared link's preview.
export const TRUST_TITLES: Record<TrustPage, string> = {
  how: ROUTES.titles.how, corrections: ROUTES.titles.corrections, pays: ROUTES.titles.pays, privacy: ROUTES.titles.privacy,
};

export const go = (path: string) => { navigate(path); window.scrollTo(0, 0); };

/** What the money pays for: ONE list, read by Who pays for this and by Support Us, so the two
 *  pages cannot come to disagree. No dollar figures and no vendor names (README, Support Us):
 *  prices and the stack change, and this should not need editing when they do. */
export const WHAT_IT_PAYS_FOR = [
  'Database and file storage for the record.',
  'The nightly job that collects the official votes, and the hosting the site runs on.',
  'The domain.',
  'Plain-language summaries of each item’s text.',
  'Ongoing development and support.',
];

/** Under every place a visitor can give. Dan's wording, approved 28 Sep 2026 when donations opened. */
export const GIFT_LEGAL = 'Gifts are not tax-deductible: Votes on Record is run by a private individual, not a registered charity. They are not political contributions: the site supports no party, candidate or campaign.';

/** A link inside the site: a real link (it can be opened in a new tab), routed in place. */
export function To({ path, children }: { path: string; children: ComponentChildren }) {
  return (
    <a href={path} onClick={(e) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault(); go(path);
    }}>{children}</a>
  );
}

const Out = ({ href, children }: { href: string; children: ComponentChildren }) => (
  <a href={href} rel="noreferrer">{children}</a>
);

function Doc({ page, lede, children }: { page: TrustPage; lede: string; children: ComponentChildren }) {
  return (
    <Pinned class="sr-doc-wrap">
      <article class="sr-doc">
        <header class="sr-doc-head">
          <div class="sr-doc-kicker">About</div>
          <h2>{TRUST_TITLES[page]}</h2>
          <button class="btn btn-ghost sr-doc-back" onClick={() => go('/')}>← Back to the record</button>
        </header>
        <p class="sr-doc-lede">{lede}</p>
        {children}
        <div class="sr-doc-end" />
      </article>
    </Pinned>
  );
}

function Section({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <section class="sr-doc-sec">
      <h3 class="sr-doc-label">{label}</h3>
      <div class="sr-doc-body">{children}</div>
    </section>
  );
}

/** How this works on summaries once Unexpected Items are published (T18, docs/rider-scan-copy-drafts.md,
 *  the labels Dan approved on 29 Sep 2026). The paragraphs on the version, the CRS summary and amendments
 *  follow it, as before. */
function ScanSummaries() {
  return (
    <>
      <P>Items carry this site’s own plain-language summary, written by an AI model, Claude Sonnet 5.5, from the
        official text of the bill, and the pop-up says so. Each summary is written in two separate passes. The
        first gives the main subject in one sentence and what the text does in a few lines, each naming the section
        it comes from and linking to it in the official text.</P>
      <P>The second pass reads every section on its own and lists, under Unexpected Items, each one that meets any
        of ten tests, grouped by the test: not related to the bill’s main subject; applies only to a specific place
        or group; a policy change made through a dollar amount; funds a specific named project or event; a different
        amount for specific places or groups; an exception or delay for a subset; delays or blocks a specific
        regulation; changes a government process; a condition defined by a narrow set of criteria; a program name
        that includes a political figure’s name. A text with only one section that does anything on its own is
        not listed for what its main subject already says, only for anything more, such as an exception. An
        exception, exemption or waiver that a section makes for a named or defined group, place, organization,
        industry, product or program is listed even when the section’s main rule is the bill’s main subject,
        including one that a spending bill repeats every year. A text of more than 100,000 characters
        is read this way twice,
        and a section either reading lists is listed. A listing describes what the section’s text does. It says
        nothing about why the section was written or whether it is good.</P>
      <P>Before anything is published, a program checks it. Every section cited must exist in the text. Every listed
        section must come with a quotation found word for word in that section, or it is dropped. Every section
        must have been read, or that part is read again. Every dollar figure must appear exactly as written in the
        section it cites. Nothing may name a political party or use a word that passes judgment. A long text with
        nothing listed is held for a person to check rather than published as having none.</P>
      <P>A summary that passes the checks is published, every listing with it, without a person reading it first;
        the site’s owner can take any one off the site. Until 3 October 2026 a person read the summary of every item
        last voted on in 2026 or later before it was published, except those written on 2 October 2026, which were
        published on 3 October after the checks alone. The pop-up says which: the day a summary was approved, or
        that it was published after the checks alone.</P>
      <P>Every summary written the earlier way was written again this way by 4 October 2026. The earlier ones are
        kept on file.</P>
    </>
  );
}

/** How this works on comparing a bill's versions (Check B, docs/version-diff-copy-drafts.md: Dan's to read
 *  before it is switched on). Shown only once comparisons are published (meta.json, versions_live). */
function VersionChangesHow() {
  return (
    <>
      <P>When one chamber passes a bill and the other changes it before the final votes, the pop-up compares the text
        the first chamber passed with the final text both chambers passed, under “Changed after House passage” (or
        Senate). It lists the sections removed before the final vote, and the sections added after the first chamber
        passed it.</P>
      <P>A program first compares the two texts word for word. A section whose wording stands in the other text is not
        listed. A section whose wording is gone may still have been kept, rewritten under another number, so for those
        sections only an AI model, Claude Sonnet 5.5, reads the section beside the sections of the other text most like
        it, and says whether one of them carries the same provision in other words: all of it, some of it, or none. It
        must quote, word for word, the passage that shows it, and a program checks the quotation; a section it cannot
        settle is not listed. When less than a tenth of the final text’s wording was in the text first passed, no AI
        model is used: the pop-up says how many of the final text’s sections were not in the first, and lists the
        sections removed.</P>
      <P>Each listed section has one line saying what it does, checked as summary lines are: dollar figures as written,
        no political party, no word that passes judgment. A comparison that passes the checks is published without a
        person reading it first, and the pop-up says so; until 3 October 2026 a person read the comparison for every
        item last voted on in 2026 or later before it was published. A comparison says what changed between two printed
        texts. It
        says nothing about why, or who sought the change.</P>
    </>
  );
}

export const P = ({ children }: { children: ComponentChildren }) => <p class="sr-doc-p">{children}</p>;

export function Bullets({ items }: { items: ComponentChildren[] }) {
  return <ul class="sr-doc-bullets">{items.map((t, i) => <li key={i}><span>{t}</span></li>)}</ul>;
}

export function TrustDoc({ page }: { page: TrustPage }) {
  if (page === 'how') return <HowThisWorks />;
  if (page === 'corrections') return <CorrectionsPage />;
  if (page === 'pays') return <WhoPays />;
  return <Privacy />;
}

// ---- How this works ---------------------------------------------------------------
// The brief: opens with Madison's line — which is already on every page, directly above,
// so the lede picks it up rather than repeating it.

function HowThisWorks() {
  // Which way summaries are written is the published record's to say (meta.json, scans_live), so this
  // page changes on the night the new method goes live (T18), not on the day a new version is deployed.
  const [meta, setMeta] = useState<Meta | null>(null);
  useEffect(() => { loadMeta().then(setMeta).catch(() => setMeta(null)); }, []);
  return (
    <Doc page="how" lede="The line above is the whole argument. Everything on this page is how the site tries to live up to it.">
      <Section label="The grid">
        <P>People down the left, voted items across the top. Each cell is one person’s final recorded vote on one
          item, in their own chamber. The newest item is on the left.</P>
        <P>The White House comes first, then the Senate, then the House. Within each, people are ordered by time in
          office, longest first, and a tie goes to alphabetical order by last name, except that the Speaker of the
          House leads the House and the Senate’s Majority Leader leads the Senate, each marked by name, because the
          two lead their chambers. Anyone who left office during the
          term stays in the grid, in a group at the foot of their chamber with the date they left, and their votes
          stay visible.</P>
        <P>You can order people by name instead, with “Order people by”; the Speaker and the Majority Leader still
          lead their chambers.</P>
        <P>The President’s row shows whether the President signed a bill, vetoed it, or let it become law without a
          signature. The Vice President’s row shows only a vote cast to break a tie in the Senate. The House’s
          members for Washington, DC and the territories are not in the grid, because they cannot vote on final
          passage; they are named beneath it.</P>
      </Section>
      <Section label="Final votes">
        <P>A chamber’s final vote on an item is its last roll call of one of these kinds: passage, including passage
          under a suspension of the rules; agreeing to a resolution; agreeing to the other chamber’s changes without
          adding more; a conference report; a confirmation; a treaty’s ratification; a verdict in an impeachment
          trial; or a veto override. A veto override, where one happens, becomes the final vote.</P>
        <P>Procedural votes are listed inside the item, not in the grid: cloture, motions to proceed, the special
          rules the House adopts for debating a measure, motions to recommit, and motions to table. So are votes on
          amendments.</P>
        <P>When the Senate confirms a group of nominees in one vote, that vote is one column, and its pop-up lists
          every nominee it confirmed. The vote before it, which only allowed the Senate to take the nominees
          together, is procedural.</P>
        <P>The House’s vote adopting its own rules at the start of a Congress has a column, because it is a
          resolution agreed to in its own right, not a rule for debating another measure.</P>
      </Section>
      <Section label="Voice votes">
        <P>Some items pass a chamber without a roll call, by voice vote or by unanimous consent (in the House, often
          worded “without objection”), so the record shows no one’s position. Where that happened, every cell in that
          chamber reads ~, which the key calls Voice vote in every case; hovering a cell says which it was, and the
          item’s pop-up quotes the official record’s own words. It is not a missing vote and not an absence: it is a
          decision the official record does not attribute to any individual.</P>
      </Section>
      <Section label="Time in office">
        <P>Days actually served in Congress and the White House: House, Senate, Vice President and President. Gaps
          are not counted, so someone who left and later returned is credited with the time served, not the time
          elapsed. House years count toward a later Senate total. The current term counts to today; a past term
          counts to its end, or through the last day the person served. A regular term counts from 3 January, when
          the Constitution begins it, and a term that ended early, by resignation or death, counts its last day, both
          as the Biographical Directory does. Whole years are complete calendar years of service, so two years in the
          House and 24 in the Senate are 26 years.</P>
        <P>Hovering a name shows the year service began, so the two figures can be checked against each other.</P>
        <P>Service dates come from the <Out href="https://github.com/unitedstates/congress-legislators">unitedstates/congress-legislators</Out> dataset,
          an open compilation of official records, and every senator and representative in every Congress on the site has been
          checked against the <Out href="https://bioguide.congress.gov/">Biographical Directory of the United States Congress</Out>.
          The two count the start of a special election or an appointment differently: the dataset from the day the
          member took the oath, the Directory from the day of the election, the appointment or the start of the term.
          That can put them days or weeks apart, up to about seven weeks, and this site keeps the dataset’s date.
          Larger differences have been corrected to the Directory’s date, as have a few smaller ones found while
          reading the Directory by hand, and each correction is listed in
          the <To path="/corrections">corrections log</To>.</P>
      </Section>
      <Section label="Summaries">
        {/* the current method unless the record says otherwise, so the page does not show the old wording
            while meta.json loads (12 Oct 2026 audit) */}
        {meta?.scans_live !== false ? <ScanSummaries /> : <>
        <P>Some items carry this site’s own plain-language summary, and more are added as each one is approved,
          the latest votes first. It is written by an AI model, Claude Sonnet 5, from the official text of the bill,
          and the pop-up says so. It gives the main subject in one sentence, then any provisions not related to the
          main subject, counted, then what the text does in up to six lines. A long text may have more lines: up to ten
          past 100,000 characters of text, and up to fifteen past 1,000,000. Every line names
          the section it comes from and links to it in the official text.</P>
        <P>Before a summary can be published, a program checks it: every section it cites must exist in the text,
          every dollar figure must appear exactly as written in the section it cites, and it may not name a
          political party or use a word that passes judgment. A summary that fails is written once more, and one
          that fails again is never published. Then a person reads it, and nothing is published until it is
          approved. The pop-up gives the day it was approved.</P>
        </>}
        <P>A summary describes one printed version of the text, and the pop-up names it: the final text passed by
          both chambers when there is one, otherwise the text as a chamber passed it, otherwise the latest version
          printed on or before the final vote, which may not be the text voted on. If the bill has changed since,
          the pop-up says so.</P>
        <P>The pop-up also shows the summary written by the Congressional Research Service, part of the Library of
          Congress, where one exists. It is the CRS’s work, not this site’s: the pop-up says so, names the version of
          the bill it describes, and says when it was written before the final vote.</P>
        <P>Amendments are never summarized. Each one with a recorded vote is listed in the item’s pop-up as the
          official record gives it, purpose and all. Nominations have no bill text, so their pop-up is built from the
          official record alone.</P>
      </Section>
      {meta?.versions_live && <Section label="Changed versions"><VersionChangesHow /></Section>}
      <Section label="What it does not do">
        <Bullets items={[
          'It does not say whether an item is good, whether it will work, or why anyone voted the way they did.',
          'It does not describe anyone’s motives, and shows party only as a letter in neutral ink.',
          'It never colours a vote. Yea and Nay are never green and red, never a check and a cross, and carry the same weight on the page.',
          'It leaves out anything the record does not state.',
        ]} />
      </Section>
      <Section label="The checks">
        <P>Before any file is published, the marks in every roll-call column are counted, chamber by chamber, and
          must equal the official tally. If a single column disagrees, nothing is published.</P>
        <P>Every signature, veto and law without signature must appear exactly once in the White House rows, and no
          cell may be left unaccounted for. Either failure stops the build the same way.</P>
        <P>Every final vote on a bill or resolution can also be compared with a second official record: the roll
          calls Congress.gov lists for that bill. A vote attached to the wrong bill would show up there. That
          comparison is run by hand, not before every build; on 15 October 2026 all 1,425 final votes were among them, three
          of them through the resolution the House agreed by (Congress.gov attaches those roll calls to the resolution,
          not to the bill).</P>
        <P>These checks have stopped builds before anything was published, which is what they are for. Fixes to
          anything that had already been published are in the <To path="/corrections">corrections log</To>.</P>
      </Section>
      <Section label="Missing data">
        <P>Shown as missing, never guessed. Where the record is silent, the site says so rather than filling the gap
          from somewhere else.</P>
      </Section>
      <Section label="Sources">
        <P>The House Clerk’s and the Senate’s roll-call records for every vote; Congress.gov for each item’s details,
          actions and summaries; the unitedstates/congress-legislators dataset for who served when; and the Census
          Bureau’s geocoder for finding a district. When senate.gov cannot be reached, a Senate roll call is read
          from the Congressional Record, the official record of the Senate’s proceedings, and the bill or nominee it
          concerns is checked against Congress.gov’s record of that same roll call. Each is linked at the foot of
          every page, beside the time the record was last updated.</P>
      </Section>
      <Section label="Never shown">
        <P>Two kinds of accurate information are deliberately left out, because placing them beside a vote makes a
          claim this site cannot back up.</P>
        <Bullets items={[
          'Campaign donations and donor data beside votes. Accurate on its own; side by side it implies cause and effect.',
          'Party-loyalty scores, advocacy-group ratings and effectiveness rankings. Each one imports somebody else’s framing.',
        ]} />
      </Section>
    </Doc>
  );
}

// ---- Corrections ----------------------------------------------------------------------
// Real entries only, read from the published corrections.json (worker/sr/corrections.py,
// from data/corrections-log.json). The empty sentence prints only once it is EARNED: a
// log that failed to load says so, rather than claiming there is nothing to correct.

type Load<T> = { k: 'loading' } | { k: 'failed'; why: string } | { k: 'loaded'; doc: T };

function CorrectionsPage() {
  const [state, setState] = useState<Load<Corrections>>({ k: 'loading' });
  const load = () => {
    setState({ k: 'loading' });
    loadCorrections()
      .then((doc) => setState({ k: 'loaded', doc }))
      .catch((e) => setState({ k: 'failed', why: String(e instanceof Error ? e.message : e) }));
  };
  useEffect(load, []);

  return (
    <Doc page="corrections" lede="Every fix, dated. Nothing is changed quietly, and nothing is removed from this log.">
      <Section label="The log">
        {state.k === 'loading' && <P>Loading the log…</P>}
        {state.k === 'failed' && (
          <>
            <P>The log could not be loaded. The file that failed: <code>{state.why}</code>. Nothing about you was
              recorded in the failure.</P>
            <button class="btn btn-secondary" onClick={load}>Try again</button>
          </>
        )}
        {state.k === 'loaded' && state.doc.entries.length === 0 && <P>No corrections yet.</P>}
        {state.k === 'loaded' && state.doc.entries.length > 0 && (
          <table class="table sr-doc-table">
            <thead><tr><th>Corrected</th><th>Item or member</th><th>What changed</th></tr></thead>
            <tbody>
              {state.doc.entries.map((e) => (
                <tr key={e.id}>
                  <td class="sr-doc-date">
                    {fmtLong(e.corrected_on)}
                    {e.before_launch && <div class="sr-doc-tag">Before the site opened</div>}
                  </td>
                  <td class="sr-doc-what">
                    {e.records.length > 0
                      ? e.records.map((r) => (
                        <div key={r.kind + r.id}>
                          <To path={r.kind === 'item' ? `/item/${r.id}` : `/member/${r.id}`}>{r.label}</To>
                        </div>
                      ))
                      : <span class="sr-muted">{e.scope}</span>}
                  </td>
                  <td>
                    {e.what_changed}
                    {e.sources.length > 0 && (
                      <div class="sr-doc-src">
                        {e.sources.length === 1 ? 'Source: ' : 'Sources: '}
                        {e.sources.map((s, i) => <span key={s.url}>{i > 0 && ' · '}<a href={s.url} rel="noreferrer">{s.label}</a></span>)}
                      </div>
                    )}
                    {(e.earlier ?? []).map((x) => (
                      <details key={x.replaced_on + x.what_changed} class="sr-doc-earlier">
                        <summary>Wording corrected on {fmtLong(x.replaced_on)}. {x.why}</summary>
                        <div>Until then it read: “{x.what_changed}”</div>
                        {x.scope && x.scope !== e.scope && <div>It described what it covered as: {x.scope}</div>}
                        {JSON.stringify(x.sources) !== JSON.stringify(e.sources) && (
                          <div>
                            {x.sources.length === 0 ? 'It named no source.' : x.sources.length === 1 ? 'Its source: ' : 'Its sources: '}
                            {x.sources.map((s, i) => <span key={s.url}>{i > 0 && ' · '}<a href={s.url} rel="noreferrer">{s.label}</a></span>)}
                          </div>
                        )}
                      </details>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      <Section label="What is listed">
        <P>Any fix to something this site has published: a vote, a tally, a date, a name, a title, or the way a figure
          is worked out. That includes fixes made before the site opened to the public, to files that had already been
          published, and any place where this site departs from a source it uses because an official record says
          otherwise.</P>
        <P>The wording of an entry is sometimes corrected, for a typo. The entry then says so, with the day and the
          reason, and keeps its earlier wording beneath it. Its date and the items or members it names never change:
          a correction to a fix is a new entry.</P>
      </Section>
      <Section label="How to report one">
        <P>Use <To path="/report">Report an error</To>, at the foot of every page or in any item’s pop-up. The form
          asks which item or member, and what is wrong, and asks you not to include personal information. No name, no
          address, no account and no IP address is stored with a report.</P>
        <P>Every report is read and checked against the official record before anything changes; nothing is applied
          as it arrives. If the record shows the site is wrong, the fix is made and listed here. If it shows the site
          is right, nothing changes.</P>
        <P>A correction is listed here whether it came from a reader, from one of the site’s own checks, or from us
          noticing it. Nobody gets a faster correction for having given money.</P>
      </Section>
    </Doc>
  );
}

// ---- Who pays for this ------------------------------------------------------------------
// D2: Dan pays personally and is named. D3: donations are "not open yet". T9: no cost figure
// anywhere on the site. T10: NOTHING ABOUT DONATIONS IS LISTED ON THE SITE, not a donor, an
// amount or a total, so no gift earns anyone recognition here; the site prefers private
// citizens, does not claim it can stop anyone giving, and no gift affects the record. This
// departs from the brief ("every funding source is disclosed") and from the design's table of
// zeros, by Dan's decision of 21 Sep 2026, and the page says why rather than going quiet.

function WhoPays() {
  return (
    <Doc page="pays" lede="Who pays for the site, and what money can and cannot do here.">
      <Section label="Who pays">
        <P>Dan Campbell, who runs the site, pays for it personally: its hosting, database, domain and development.
          Gifts help.</P>
      </Section>
      <Section label="Donations">
        <P>Anyone can give, through Stripe, from <To path="/support">Support Us</To>. The site prefers gifts from
          private citizens, but it has no honest way to stop a political party, a campaign, a member of Congress or a
          lobbyist from giving, and it does not claim to.</P>
        <P>{GIFT_LEGAL}</P>
      </Section>
      <Section label="What a gift cannot do">
        <P>Every gift is recorded the same way, whoever it comes from. No gift changes what the site shows or how it
          reports the record. Money does not buy placement, an early look at a summary, or a faster correction.</P>
      </Section>
      <Section label="Why donors are not listed">
        <P>This site does not list donations: no names, no amounts and no totals. Naming a donor gives them public
          recognition from the site, and a prominent donor, such as a member of Congress, would get it whether the
          site meant to give it or not. Listing no one is the only way to give no one that.</P>
        <P>It is not a perfect answer, because it means you cannot see here who gives. It is the safest one.</P>
      </Section>
      <Section label="What it pays for">
        <Bullets items={WHAT_IT_PAYS_FOR} />
      </Section>
      <Section label="Why no ads">
        <P>Two reasons, and either one would be enough. An ad network sets its own tracking cookies, which breaks the
          promise that this site stores nothing about you. And a political advertisement beside a vote record reads
          as an endorsement, whatever the disclaimer says.</P>
      </Section>
      <Section label="When this changes">
        <P>If anything about how the site is paid for changes, this page will say so.</P>
      </Section>
    </Doc>
  );
}

// ---- Privacy ------------------------------------------------------------------------------
// The brief: short and plain, and it names the hosts' logs honestly. CLAUDE.md: no visitor
// accounts exist, and one editor account does — say so.

function Privacy() {
  return (
    <Doc page="privacy" lede="The short version: this site stores nothing about you, and its own code sets no cookies. Cloudflare, which carries the site’s traffic and protects it from attacks, sets one security cookie only if it asks your browser to pass a check first, explained below. The rest of this page is those sentences, checked.">
      <Section label="Not collected">
        <Bullets items={[
          'No visitor accounts. There is nothing to sign up for.',
          'No cookies set by this site. Cloudflare sets one, for security, only if it asks your browser to pass a check: see “Security cookies” below.',
          'No advertising, and no ad scripts.',
          'No analytics scripts, no session recording, no heatmaps.',
          'No social media embeds. The header and the footer link to this site’s accounts on X and LinkedIn; the header shows each as a small mark drawn by this site, and nothing is loaded from them unless you follow a link, and their own privacy rules apply once you do.',
          'The Share buttons for X, Facebook, LinkedIn, Bluesky and Threads are plain links. Nothing is loaded from or sent to them unless you press one, and their own privacy rules apply once you do. Instagram has no share link for websites, so its button saves the picture to your device and copies the wording; nothing is sent to Instagram.',
          'Nothing loaded from another company’s address, fonts included. Loading a font from another company’s server hands them your address; every font here is served from this site.',
        ]} />
      </Section>
      <Section label="Security cookies">
        <P>Cloudflare, which carries this site’s traffic, protects it from attacks. If it judges that a request may
          come from an automated program, it can show your browser a short check before passing the request on. Only
          then does it set a cookie, cf_clearance: a record that your browser passed the check, so it is not asked
          again for a while. It holds no name, address or account. An ordinary visit is not checked and gets no
          cookie (none was set on about 5,900 responses read on 12 October 2026).</P>
        <P>This site’s own code does not read the cookie or use it for anything, and clearing your browser’s cookies
          removes it. Cloudflare describes it on <Out href="https://developers.cloudflare.com/fundamentals/reference/policies-compliances/cloudflare-cookies/">its
          own page about its cookies</Out>.</P>
      </Section>
      <Section label="One account">
        <P>One account exists: the editor’s. The site’s owner uses it, at a separate address this site does not link
          to, to read error reports and check each against the official record, to add entries to the Corrections
          log, and to read the AI summaries, holding or taking down any that are wrong. It holds nothing about
          visitors.</P>
      </Section>
      <Section label="Your district">
        <P>If you look up who represents you, your district is kept in your own browser’s local storage. It is never
          sent to the server. Clearing your browser data removes it, and nothing here remembers that it was ever
          set.</P>
      </Section>
      <Section label="Address lookup">
        <P>When you press Find, the address you typed is sent through this site’s own server to the Census Bureau’s
          geocoder, a federal service, which returns the congressional district. The server passes it on without
          storing it, its request logging is off, and the address never goes into a web address, because web
          addresses end up in logs. The Census Bureau receives it from this site’s server, not from your
          browser.</P>
        <P>The server asks once for each Congress on this site, on that Congress’s own district lines, because lines
          change between Congresses: the 2023–2024 record pins whoever represented that address then. Only the
          district comes back to your browser to be kept; the address is not kept anywhere.</P>
      </Section>
      <Section label="Use my location">
        <P>Your browser asks your permission first. Then it downloads a map of every congressional district, the
          same file for everyone who presses the button, and finds your district in it, inside the browser. Your
          position is never sent anywhere, and it is not kept once your district is found; only the district is,
          as above.</P>
        <P>The map is the Census Bureau’s district lines, simplified to within about 25 metres so the file is small
          enough for a phone. When your position is closer to a line than that, allowing for how precisely your
          browser knows where you are, the page does not guess: it says so and asks which district is yours, or you
          can use the address box for an exact answer.</P>
      </Section>
      <Section label="Error reports">
        <P>A report stores which item or member it is about, your message, and when it arrived. No name, no IP
          address, no identifier. The form asks you not to include personal information, because anything you type
          there is kept. The database that holds them is copied every night to private storage with Cloudflare, which
          also carries the site’s traffic, so the record can be restored if the database is ever lost. Each night’s
          copy is deleted after 14 days; the copy made on the first of each month is kept for a year.</P>
        <P>To stop one network flooding the form or the lookup, the server counts how many requests come from each
          network in ten minutes. It counts a scrambled form of the network’s address, made with a key that exists
          only in the running server and is never written down, so without it the count cannot be turned back into
          the address. The count is held in memory, never on disk, and forgotten within about a minute of its ten
          minutes passing.</P>
      </Section>
      <Section label="Donations">
        <P>Gifts are made on Stripe’s own payment page, which Support Us links to; nothing from Stripe loads on this
          site. Stripe’s page asks for your email address and your payment details, and this site never sees any of
          them. Stripe keeps them under <Out href="https://stripe.com/privacy">its own privacy policy</Out>. Stripe
          may also offer to save your details for faster checkout with its Link service; that choice is between you
          and Stripe.</P>
        <P>The site’s owner sees each gift in Stripe’s dashboard: its amount, its date and the details Stripe’s page
          collected. None of it is listed on this site, and none of it reaches the record.</P>
      </Section>
      <Section label="What we cannot promise">
        <P>The companies this site runs on keep standard server logs, the same as for any website. Railway, which
          runs the server, keeps a record of every request for 30 days: the time, the page or file asked for, the
          network (IP) address it came from, and the browser’s name. It does not record what you type into the
          site. Cloudflare, which carries the site’s traffic, handles every request, including the network (IP)
          address it came from, the page asked for and the browser’s name. On the free plan this site uses,
          Cloudflare keeps a record of individual requests for 31 days (a sample of them when traffic is heavy): the
          time, the page asked for, the network (IP) address, the country and the browser’s name, and for requests
          its security checks act on, the page the visitor came from. It keeps daily totals for a year. The site’s
          owner can look these up in Cloudflare’s dashboard. How long Cloudflare itself keeps them beyond that is set
          out in <Out href="https://www.cloudflare.com/privacypolicy/">Cloudflare’s own privacy policy</Out>, which
          names no fixed period. The site’s owner looks only at totals, such as how many times a page was opened by
          day, page, browser type or country, to see how the site is used. The owner never looks up an individual
          visitor’s address or requests, and never joins these records to anything. The record itself is stored
          with Supabase, which your
          browser never contacts. Once you press Find, what the Census Bureau does with the address is governed by
          its own privacy policy.</P>
        <P>That is a limit worth stating plainly, rather than claiming an absolute this site cannot deliver.</P>
      </Section>
    </Doc>
  );
}
